import { FC, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import {
  faCheckCircle,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import {
  createBemBlockBuilder,
  InPageNotification,
  Loading,
} from '@ebsco-ui/ebsco-ui';

import { Modal, ModalHeader, Notification, Toast } from '@app/components';
import {
  ProjectCreationDto,
  queryKeys,
  useEditProjectsMutation,
} from '@app/hooks';
import { sharedMessages } from '@app/translations';

import { ProjectModalForm } from './ProjectModalForm';

import './ProjectModal.scss';

interface EditProjectModalProps {
  isOpen: boolean;
  id: string;
  values: {
    name: string;
    description: string;
  };
  onClose: () => void;
}

const cnBem = createBemBlockBuilder(['projectModal']);

export const EditProjectModal: FC<EditProjectModalProps> = ({
  isOpen,
  id,
  values,
  onClose,
}) => {
  const queryClient = useQueryClient();
  const { $t } = useIntl();
  const editProjectMutation = useEditProjectsMutation({ id });

  const handleSubmit = (values: ProjectCreationDto) =>
    editProjectMutation.mutate(values);

  useEffect(() => {
    if (editProjectMutation.isSuccess) {
      onClose();
      queryClient.refetchQueries([queryKeys.projectDetails], {
        type: 'active',
      });

      Toast({
        type: 'success',
        title: $t({
          id: 'projects.projectHasBeenEdited',
          defaultMessage: 'Your project has been edited.',
        }),
        icon: faCheckCircle,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [$t, editProjectMutation.isSuccess]);

  const handleAfterClose = () => {
    editProjectMutation.reset();
  };

  const renderErrorNotification = () => (
    <Notification
      variant={InPageNotification.VARIANT.error}
      icon={faExclamationTriangle}
      className={cnBem('__notification')}
      header={$t(sharedMessages.somethingWentWrong)}
    />
  );

  const isLoading =
    editProjectMutation.isLoading || editProjectMutation.isSuccess;

  const renderBody = () => {
    if (isLoading) {
      return (
        <Modal.Body>
          <Loading loadingMessage={$t(sharedMessages.loadingMessage)} />
        </Modal.Body>
      );
    }

    return (
      <ProjectModalForm
        values={values}
        onSubmit={handleSubmit}
        onClose={onClose}
      >
        {editProjectMutation.isError && renderErrorNotification()}
      </ProjectModalForm>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      modalTitle={$t(sharedMessages.editProject)}
      className={cnBem()}
      onClose={onClose}
      onAfterClose={handleAfterClose}
    >
      <ModalHeader
        title={$t(sharedMessages.editProject)}
        className={cnBem('__header')}
        onClose={onClose}
      />
      {renderBody()}
    </Modal>
  );
};
